
import React from 'react';
import { Answer, RecommendedAction } from '@/types/assessment';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { List } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface MaturityAnswersProps {
  categoryId: string;
  questionId: string;
  answers: Answer[];
  onUpdateAnswer: (categoryId: string, questionId: string, answerId: string, updates: Partial<Answer>) => void;
  onOpenRecommendations: (answerId: string) => void;
}

export function MaturityAnswers({ 
  categoryId, 
  questionId, 
  answers, 
  onUpdateAnswer,
  onOpenRecommendations
}: MaturityAnswersProps) {
  // Sort answers by value to ensure correct order (0, 1, 2, 3, 4)
  const sortedAnswers = [...answers].sort((a, b) => a.value - b.value);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Maturity Levels</Label>
        <div className="text-xs text-muted-foreground">Fixed 5 levels (0-4)</div>
      </div>
      
      {sortedAnswers.map((answer) => (
        <div key={answer.id} className="flex items-center gap-3 border rounded-md p-2">
          <div className="w-24">
            <Badge 
              className="w-full flex justify-center py-1 px-3 bg-orange-500 hover:bg-orange-600 text-white border-none"
            >
              Level {answer.value}
            </Badge>
          </div>
          <div className="flex-1">
            <Input
              value={answer.text}
              onChange={(e) => onUpdateAnswer(
                categoryId, 
                questionId, 
                answer.id, 
                { text: e.target.value }
              )}
              placeholder={`Level ${answer.value} description`}
            />
          </div>
          <div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onOpenRecommendations(answer.id)}
              className="flex items-center gap-1 min-w-[60px] justify-center"
              title="Manage recommendations"
            >
              <List className="h-4 w-4" />
              <Badge variant="outline" className="text-xs ml-1 bg-gray-100">
                {answer.recommendedActions?.length || 0}
              </Badge>
            </Button>
          </div>
        </div>
      ))}

      <p className="text-xs text-muted-foreground italic">
        Maturity levels are fixed with values 0-4. You can customize the descriptions but not add or remove levels.
      </p>
    </div>
  );
}
